import React from 'react';
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { Reveal } from './Reveal';
const before = ['Spending hours hunting for worksheets', 'Not sure if you’re teaching in the right order', 'Nervous about your own Spanish', 'No assessments or answer keys', 'Different resources that don’t connect'];
const after = ['A full year mapped out, page by page', 'Every lesson explained in plain English', 'Answer keys and rubrics included', 'Print or teach on screen in one click', 'One connected 10-volume journey'];
const highlights = [['600', 'PAGES OF DONE-FOR-YOU CONTENT'], ['A1 → B2', 'FULL PROGRESSION'], ['100%', 'CLASSROOM-TESTED STRUCTURE']];
export function TransformationSection() {
  return <section className="bg-brand-main px-4 py-20 md:py-28" data-id="element-168" data-mp="299w4d4u59pnz">
      <Reveal className="mx-auto max-w-5xl" data-id="element-169" data-mp="247x2jze1z0t5">
        <div className="mx-auto max-w-3xl text-center" data-id="element-170" data-mp="24b2pp934fsm8">
          <p className="mb-4 text-xs font-extrabold tracking-[0.18em] text-brand-teal" data-id="element-171" data-mp="208fxv86ga7p1">
            A BETTER WAY TO TEACH SPANISH
          </p>
          <h2 className="text-4xl font-black leading-[1.08] tracking-tight text-brand-blue md:text-5xl" data-id="element-172" data-mp="f99b3midx13j">
            From an Overwhelmed Teacher to a{' '}
            <span className="italic text-brand-purple" data-id="element-173" data-mp="5dxueynf1jt5">
              Confident Spanish Guide.
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-brand-gray md:text-base" data-id="element-174" data-mp="ac93v89stsau">
            Spanish Ready turns hours of planning into one open-and-teach
            system. Walk into every lesson with a clear plan, real speaking
            practice, and the confidence to teach the language well.
          </p>
        </div>

        <div className="mx-auto my-12 grid max-w-3xl divide-y divide-brand-dark overflow-hidden rounded-2xl border border-brand-dark bg-brand-alt shadow-sm sm:grid-cols-3 sm:divide-x sm:divide-y-0" data-id="element-175" data-mp="t1xu4o2pgkjv">
          {highlights.map(([value, label]) => <div key={label} className="px-5 py-5 text-center" data-id="element-176" data-mp="3du14fb203kkn">
              <strong className="block font-serif text-3xl leading-none text-brand-orange" data-id="element-177" data-mp="1k8xqv6c112ow">
                {value}
              </strong>
              <span className="mt-2 block text-[10px] font-extrabold leading-tight tracking-[0.08em] text-brand-gray" data-id="element-178" data-mp="1fcz9i2p48kxz">
                {label}
              </span>
            </div>)}
        </div>

        <div className="overflow-hidden rounded-3xl border border-brand-dark bg-brand-alt shadow-[0_18px_45px_rgba(22,56,107,0.10)]" data-id="element-179" data-mp="15j8o56oacqmi">
          <div className="grid md:grid-cols-2" data-id="element-180" data-mp="wjvcq8glprz5">
            <Comparison title="BEFORE" items={before} positive={false} data-id="element-181" data-mp="wj3gqwj3ds8c" />
            <Comparison title="WITH SPANISH READY" items={after} positive data-id="element-182" data-mp="rm3aakibyngp" />
          </div>
          <div className="border-t border-brand-dark bg-brand-main px-6 py-4 text-center text-xs font-semibold text-brand-gray" data-id="element-183" data-mp="mna8hpceokwd">
            A complete Spanish curriculum that helps you prepare less and teach
            with more confidence.
          </div>
        </div>
      </Reveal>
    </section>;
}
function Comparison({
  title,
  items,
  positive
}: {
  title: string;
  items: string[];
  positive: boolean;
}) {
  const Icon = positive ? CheckCircle2Icon : XCircleIcon;
  return <div className={`p-6 md:p-9 ${positive ? 'border-t border-brand-dark bg-brand-alt md:border-l md:border-t-0' : 'bg-[#f5f0e8]'}`} data-id="element-184" data-mp="2y9apby60bsth">
      <div className="mb-7 flex items-start justify-between gap-4" data-id="element-185" data-mp="33arn3kt72z5u">
        <div data-id="element-186" data-mp="26jppzp372aai">
          <p className={`text-[10px] font-extrabold tracking-[0.16em] ${positive ? 'text-brand-teal' : 'text-brand-gray'}`} data-id="element-187" data-mp="26jz6mm2cn53j">
            {positive ? 'THE READY-TO-TEACH SYSTEM' : 'THE COST OF STARTING FROM SCRATCH'}
          </p>
          <h3 className="mt-2 font-serif text-2xl font-bold tracking-tight text-brand-blue" data-id="element-188" data-mp="2kj7ahs9gj1e2">
            {title}
          </h3>
        </div>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${positive ? 'bg-brand-teal text-white shadow-md' : 'bg-brand-alt text-brand-gray'}`} data-id="element-189" data-mp="3r0lyrz1s2hoi">
          <Icon className="h-5 w-5" aria-hidden="true" data-id="element-190" data-mp="18qzl7kx7fpbc" />
        </div>
      </div>
      <ul className="space-y-2.5" data-id="element-191" data-mp="13rmweumrw864">
        {items.map(item => <li key={item} className={`flex items-center gap-3 rounded-xl px-3 py-3 ${positive ? 'bg-brand-main' : 'bg-brand-alt/70'}`} data-id="element-192" data-mp="1hqml22aacn2a">
            <Icon className={`h-4 w-4 shrink-0 ${positive ? 'text-brand-teal' : 'text-brand-gray'}`} aria-hidden="true" data-id="element-193" data-mp="1rnhiehutohez" />
            <span className={`text-sm leading-snug ${positive ? 'font-semibold text-brand-text' : 'text-brand-gray'}`} data-id="element-194" data-mp="1moeej0qkwh8l">
              {item}
            </span>
          </li>)}
      </ul>
    </div>;
}